import {
  ConnectorStandard,
  CurrentType,
  OperatorSlug,
  PaymentMethod,
  PointAvailability,
  VoltageClass,
} from '@ev/shared'
import type * as ft from 'funtypes'
import { operatorKey as foldOperatorName } from '../search/untracked'
import { EMPTY_FILTERS, type Filters } from './filters'

/**
 * Query-string codec, so any filtered view is a shareable link.
 *
 * Decoding is total: an unknown or malformed value is dropped rather than
 * throwing, because the input is a URL a stranger typed. Encoding omits
 * anything at its default, so the unfiltered view has an empty query string.
 */

export interface UrlState {
  readonly filters: Filters
  readonly query: string
  readonly selectedSiteId: string | null
}

export const EMPTY_URL_STATE: UrlState = {
  filters: EMPTY_FILTERS,
  query: '',
  selectedSiteId: null,
}

/** Query-string parameter names. Frozen: these appear in shared links. */
export const URL_PARAMS = {
  operators: 'op',
  operatorNames: 'opn',
  connectors: 'conn',
  currentTypes: 'cur',
  minPowerKw: 'pmin',
  voltageClasses: 'v',
  availability: 'av',
  paymentMethods: 'pay',
  query: 'q',
  selectedSiteId: 'site',
} as const

const LIST_SEPARATOR = ','

const encodeList = (values: readonly string[]): string | null =>
  values.length === 0 ? null : values.join(LIST_SEPARATOR)

/** Split, trim, drop anything the runtype rejects, and deduplicate. */
const decodeList = <T>(raw: string | null, runtype: ft.RuntypeBase<T>): T[] => {
  if (raw === null || raw === '') return []
  const accepted: T[] = []
  raw
    .split(LIST_SEPARATOR)
    .map((entry) => entry.trim())
    .forEach((entry) => {
      if (entry !== '' && runtype.test(entry) && !accepted.includes(entry as T))
        accepted.push(entry as T)
    })
  return accepted
}

/**
 * The untracked-operator facet, which has no closed union to check against.
 *
 * Its values are folded names, so the fold *is* the validation: anything a
 * stranger typed comes back as the same shape a click would have produced, and
 * a name longer than any real one is dropped rather than carried into a `Set`.
 * A value that matches no network filters nothing away, exactly as an unknown
 * slug does.
 */
const MAX_OPERATOR_NAME_LENGTH = 80

const decodeNames = (raw: string | null): string[] => {
  if (raw === null || raw === '') return []
  const accepted: string[] = []
  raw
    .split(LIST_SEPARATOR)
    .map((entry) => foldOperatorName(entry))
    .forEach((entry) => {
      if (entry !== '' && entry.length <= MAX_OPERATOR_NAME_LENGTH && !accepted.includes(entry))
        accepted.push(entry)
    })
  return accepted
}

const decodeNumber = (raw: string | null): number | null => {
  if (raw === null || raw.trim() === '') return null
  const value = Number(raw)
  return Number.isFinite(value) && value >= 0 ? value : null
}

/** Serialise to a query string *without* the leading `?`. */
export const encodeUrlState = (state: UrlState): string => {
  const params = new URLSearchParams()

  const put = (key: string, value: string | null): void => {
    if (value !== null && value !== '') params.set(key, value)
  }

  put(URL_PARAMS.operators, encodeList(state.filters.operators))
  put(URL_PARAMS.operatorNames, encodeList(state.filters.operatorNames))
  put(URL_PARAMS.connectors, encodeList(state.filters.connectors))
  put(URL_PARAMS.currentTypes, encodeList(state.filters.currentTypes))
  put(URL_PARAMS.voltageClasses, encodeList(state.filters.voltageClasses))
  put(URL_PARAMS.availability, encodeList(state.filters.availability))
  put(URL_PARAMS.paymentMethods, encodeList(state.filters.paymentMethods))
  put(
    URL_PARAMS.minPowerKw,
    state.filters.minPowerKw === null ? null : String(state.filters.minPowerKw),
  )
  put(URL_PARAMS.query, state.query.trim())
  put(URL_PARAMS.selectedSiteId, state.selectedSiteId)

  return params.toString()
}

/** Parse a query string (with or without the leading `?`). Never throws. */
export const decodeUrlState = (search: string): UrlState => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)

  return {
    filters: {
      operators: decodeList(params.get(URL_PARAMS.operators), OperatorSlug),
      operatorNames: decodeNames(params.get(URL_PARAMS.operatorNames)),
      connectors: decodeList(params.get(URL_PARAMS.connectors), ConnectorStandard),
      currentTypes: decodeList(params.get(URL_PARAMS.currentTypes), CurrentType),
      minPowerKw: decodeNumber(params.get(URL_PARAMS.minPowerKw)),
      voltageClasses: decodeList(params.get(URL_PARAMS.voltageClasses), VoltageClass),
      availability: decodeList(params.get(URL_PARAMS.availability), PointAvailability),
      paymentMethods: decodeList(params.get(URL_PARAMS.paymentMethods), PaymentMethod),
    },
    query: params.get(URL_PARAMS.query)?.trim() ?? '',
    selectedSiteId: params.get(URL_PARAMS.selectedSiteId) || null,
  }
}
